import { ReactNode, useEffect, useState } from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { getCachedFeatureFlags, loadFeatureFlags } from '../services/FeatureFlagService';

interface FeatureGateProps {
  featureKey: string;
  children: ReactNode;
}

function FeatureGate({ featureKey, children }: FeatureGateProps) {
  const cachedFlags = getCachedFeatureFlags();
  const [isLoading, setIsLoading] = useState(!cachedFlags);
  const [isEnabled, setIsEnabled] = useState(cachedFlags?.isEnabled(featureKey) ?? false);

  useEffect(() => {
    let mounted = true;

    const loadFlag = async () => {
      const flags = await loadFeatureFlags();
      if (!mounted) return;
      const nextEnabled = flags.isEnabled(featureKey);
      if (isEnabled !== nextEnabled) {
        setIsEnabled(nextEnabled);
      }
      setIsLoading(false);
    };

    loadFlag();

    return () => {
      mounted = false;
    };
  }, [featureKey, isEnabled]);

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography role="status">Checking feature availability...</Typography>
      </Container>
    );
  }

  if (!isEnabled) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography role="alert">This feature is currently unavailable.</Typography>
      </Container>
    );
  }

  return <>{children}</>;
}

export default FeatureGate;
